/**
 * Wrecks of the expansion's guns, works and buildings (10 §2, missions 13, 15, 16, 19, 21, 30). All
 * in the prop's own frame and - the fallen mast apart - inside the old footprint.
 */

import type { Pcg32 } from '../../sim/rng';
import type { Palette } from '../palette';
import { CASEMATE_ROOF_H, CASEMATE_WALL_H } from './artillery';
import { PIER_FOOTING, WATERLINE } from './boats';
import { MIN_BAR_METRES } from './budgets';
import type { KitBuilder } from './flora';
import { MeshBuilder, place } from './MeshBuilder';
import { strut, tubeZ, wheel } from './parts';
import { prism } from './shapes';
import { bag } from './structures';
import { burnt, debris, scorch } from './wreckParts';

const TAU = Math.PI * 2;

/** Field gun: tube blown off its cradle and lying askew, shield flat, one wheel down, trails splayed. */
export const fieldGunWreck: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  scorch(mb, p, 2.8, 2.8);
  mb.box(0.5, 0.4, 1.0, burnt(p), place(0, 0.15, 0.1, 0.2, 1, 0, 0.25));
  mb.box(1.5, 0.08, 0.95, burnt(p), place(0.15, 0.08, 1.0, 0.25, 1, 0.12));
  mb.with(place(-0.35, 0.2, -0.7, rng.range(0.5, 0.9), 1, Math.PI / 2 - 0.04), () => {
    mb.cylinder(0.13, 0.1, 2.0, 6, p.color('metal.lo'));
    mb.cylinder(0.17, 0.17, 0.25, 6, p.color('ink.1'), place(0, 2.0, 0), p.color('ink.0'));
  });
  mb.cylinder(0.6, 0.6, 0.22, 8, p.color('ink.1'), place(1.0, 0, -0.4), p.color('ink.0'));
  mb.with(place(-0.95, 0, 0.15, 0, 1, 0, 0.35), () => wheel(mb, p, [0, 0.55, 0], 0.6, 0.22, 8, 'ink.0'));
  for (const sx of [-1, 1]) strut(mb, [sx * 0.3, 0.3, -0.05], [sx * 1.3, 0.1, -1.45], 0.17, p.color('ink.1'));
  debris(mb, p, rng, 3, 1.2, 1.2);
  return mb;
};

/** Gun pit: the ready ammunition has gone up - the floor is black, the bags burst outward, only a course or so left. */
export const gunPitWreck: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  mb.box(4.4, 0.1, 3.1, p.color('ink.1'), place(0, 0, 0.65));
  const run = (x0: number, z0: number, x1: number, z1: number): void => {
    const n = Math.round(Math.hypot(x1 - x0, z1 - z0) / 0.74);
    for (let i = 0; i < n; i++) {
      if (rng.chance(0.3)) continue;
      const t = (i + 0.5) / n;
      const tone = { all: p.color(rng.chance(0.5) ? 'canvas.lo' : 'ink.1'), top: p.color('canvas.mid') };
      const x = x0 + (x1 - x0) * t;
      const z = z0 + (z1 - z0) * t;
      bag(mb, 0.72, 0.46, tone, place(x + rng.range(-0.12, 0.12), 0, z + rng.range(-0.12, 0.12), rng.range(0, TAU)));
      if (rng.chance(0.35)) bag(mb, 0.72, 0.46, tone, place(x, 0.29, z, rng.range(0, TAU)));
    }
  };
  run(-2.4, 2.35, 2.4, 2.35);
  run(-2.25, 2.0, -2.25, -0.6);
  run(2.25, 2.0, 2.25, -0.6);
  for (const x of [-1.4, 1.3]) mb.box(0.9, 0.25, 0.55, burnt(p), place(x, 0.08, 1.6, rng.range(-0.5, 0.5), 1, 0.1));
  // Scattered about the middle of the PIT, which lies a metre south of the prop's origin.
  mb.with(place(0, 0, 0.9), () => debris(mb, p, rng, 5, 1.7, 1.2));
  return mb;
};

/** One fallen length of lattice lying along +z of the current frame, from mast station `s0` to `s1`, metres. */
function fallenLattice(mb: MeshBuilder, p: Palette, s0: number, s1: number, z0: number): void {
  const half = (s: number): number => 0.55 - (0.39 * s) / 11.2;
  const z1 = z0 + (s1 - s0);
  for (const sx of [-1, 1]) {
    for (const up of [0, 1]) {
      const from = [sx * half(s0), up * half(s0) * 2 + 0.06, z0] as const;
      strut(mb, from, [sx * half(s1), up * half(s1) * 2 + 0.06, z1], MIN_BAR_METRES, p.color(up ? 'metal.lo' : 'ink.1'));
    }
  }
  // The collars that banded it, still red and white: what says "mast" when it is flat on the ground.
  const bay = 11.2 / 6;
  for (let i = 1; i <= 6; i++) {
    const s = bay * i;
    if (s < s0 || s > s1) continue;
    const w = half(s) * 2 + 0.2;
    mb.box(w, w, 0.16, p.color(i % 2 ? 'enemyAccent.0' : 'paper.0'), place(0, 0, z0 + s - s0));
    const dir = i % 2 ? 1 : -1;
    const back = Math.max(s0, s - bay);
    const from = [-dir * half(back), half(back) * 2 + 0.06, z0 + back - s0] as const;
    strut(mb, from, [dir * half(s), half(s) * 2 + 0.06, z0 + s - s0], MIN_BAR_METRES, p.color('metal.lo'));
  }
}

/**
 * Relay mast: FALLEN ACROSS THE HILLTOP - the one wreck that leaves its footprint. The foot and four
 * bent stumps stand; the mast lies toward the south (so the camera sees all of it), broken-backed,
 * its drums crushed under it and its guys slack on the ground beside their anchors.
 */
export const relayMastWreck: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  mb.box(1.7, 0.25, 1.7, { all: p.color('concrete.lo'), top: p.color('ink.1') });
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const lean = [sx * 0.55 + rng.range(-0.3, 0.3), rng.range(0.7, 1.3), sz * 0.55 + rng.range(0.0, 0.4)] as const;
      strut(mb, [sx * 0.55, 0.25, sz * 0.55], lean, MIN_BAR_METRES, p.color('ink.1'));
    }
  }
  const fall = rng.range(-0.7, 0.7);
  const kink = rng.range(0.25, 0.45) * (rng.chance(0.5) ? 1 : -1);
  mb.with(place(0, 0, 0, fall), () => {
    fallenLattice(mb, p, 1.2, 6.2, 1.0);
    mb.with(place(0, 0, 6.0, kink), () => {
      fallenLattice(mb, p, 6.2, 11.2, 0.1);
      mb.cylinder(0.46, 0.46, 0.3, 8, p.color('metal.lo'), place(0.5, 0, 2.3, 0, 1, 0.2), p.color('paper.0'));
      mb.cylinder(0.46, 0.46, 0.3, 8, p.color('ink.1'), place(-0.45, 0, 3.4, 0, 1, -0.15), p.color('metal.lo'));
      mb.box(0.26, 0.26, 0.26, p.color('ink.1'), place(0, 0.05, 5.3));
    });
  });
  const out = 3.3 / Math.SQRT2;
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      mb.box(0.5, 0.3, 0.5, p.color('concrete.lo'), place(sx * out, 0, sz * out));
      const end = [sx * out * rng.range(0.2, 0.5), 0.04, sz * out * rng.range(0.1, 0.6) + 0.8] as const;
      strut(mb, [sx * out, 0.2, sz * out], end, MIN_BAR_METRES, p.color('ink.1'));
    }
  }
  return mb;
};

/** Generator hut: walls down to stubs round the burnt-out set, the roof sheet thrown against it, the drum split. */
export const generatorHutWreck: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  scorch(mb, p, 3.5, 2.9, -0.1);
  const x = -0.5;
  mb.box(2.6, 0.1, 2.6, p.color('ink.1'), place(x, 0.05, 0));
  mb.box(2.5, rng.range(0.6, 1.1), 0.12, burnt(p), place(x, 0.1, -1.2));
  mb.box(0.12, rng.range(0.4, 0.9), 2.2, burnt(p), place(x - 1.2, 0.1, 0));
  mb.box(0.9, rng.range(0.5, 1.2), 0.12, burnt(p), place(x - 0.8, 0.1, 1.2));
  // The set itself: engine block and the alternator's drum, which is how you know what stood here.
  mb.box(1.4, 0.8, 0.7, { all: p.color('ink.1'), top: p.color('metal.lo') }, place(x - 0.1, 0.15, -0.1));
  tubeZ(mb, x + 0.2, 0.55, 0.3, 1.0, 0.32, 0.32, 8, p.color('metal.lo'), p.color('ink.0'));
  mb.box(2.4, 0.08, 2.0, { all: p.color('ink.1'), top: p.color('roof.0') }, place(0.55, 0.75, 0.2, 0.1, 1, 0, -0.7));
  strut(mb, [x - 0.8, 0.2, -0.8], [x + 0.5, 0.12, -1.35], 0.26, p.color('ink.1'));
  for (const dz of [-0.35, 0.4]) {
    mb.box(0.7, 0.06, 0.6, p.color('enemyAccent.0'), place(1.3, 0.1, dz, rng.range(0, TAU), 1, rng.range(0.2, 0.5)));
  }
  debris(mb, p, rng, 4, 1.5, 1.2);
  return mb;
};

type Outline = ReadonlyArray<readonly [number, number]>;
const CASEMATE_WEST: Outline = [[-3.2, -2.5], [0.0, -2.5], [-0.3, 2.7], [-2.2, 2.7], [-3.2, 1.7]];
const CASEMATE_EAST: Outline = [[0.6, -2.5], [3.2, -2.5], [3.2, 1.7], [2.2, 2.7], [0.3, 2.7]];

/** Casemate: CRACKED OPEN, not flattened - split through the gun room, the halves leaning apart, the roof broken. */
export const casemateWreck: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  scorch(mb, p, 7.0, 6.2);
  const concrete = { top: p.color('ink.1'), side: p.color('concrete.mid') };
  mb.box(1.1, 1.5, 4.9, p.color('ink.0'), place(0.15, 0, 0.1));
  mb.with(place(-0.1, -0.06, 0, 0, 1, 0, 0.07), () => prism(mb, CASEMATE_WEST, 0, CASEMATE_WALL_H, concrete));
  mb.with(place(0.1, -0.08, 0, 0, 1, 0, -0.09), () => prism(mb, CASEMATE_EAST, 0, CASEMATE_WALL_H, concrete));
  // Roof: a slab still on each half, tipped with it, and the middle one fallen into the break.
  const slab = { all: p.color('concrete.hi'), top: p.color('concrete.mid') };
  const h = CASEMATE_ROOF_H;
  mb.box(2.9, h, 4.9, slab, place(-2.05, CASEMATE_WALL_H - 0.2, 0, 0.04, 1, 0.03, 0.1));
  const sooted = { ...slab, top: p.color('ink.1') };
  mb.box(2.6, h, 4.6, sooted, place(2.25, CASEMATE_WALL_H - 0.3, 0.1, -0.05, 1, -0.04, -0.13));
  mb.box(1.5, h, 2.6, slab, place(0.2, 0.9, 0.3, rng.range(-0.2, 0.2), 1, 0.2, 0.55));
  // The gun, dropped out of its embrasure; the rear door blown off its hinges and propped against the wall.
  const droop = place(0.1, 1.15, 2.45, 0.15, 1, Math.PI / 2 + 0.4);
  mb.cylinder(0.2, 0.16, 1.2, 6, p.color('metal.lo'), droop);
  mb.box(1.1, 1.8, 0.12, burnt(p), place(0.5, 0, -3.5, 0.1, 1, 0.5));
  debris(mb, p, rng, 8, 3.0, 2.7);
  return mb;
};

/**
 * Bridge pier: the head blown off its footing. What is left stands lower than the deck it carried - jagged
 * stumps of concrete on a cracked, scorched footing - with a slab of the head tipped into the river beside it
 * and the rest lying as rubble round the foot, awash.
 */
export const bridgePierWreck: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  prism(mb, PIER_FOOTING, 0, 0.45, { top: p.color('ink.1'), side: p.color('rock.lo') });
  const stub = { all: p.color('concrete.mid'), top: p.color('concrete.hi') };
  mb.box(0.8, rng.range(0.2, 0.32), 0.9, stub, place(-0.4, 0.45, -0.25));
  mb.box(0.7, rng.range(0.08, 0.2), 0.8, stub, place(0.4, 0.45, 0.2));
  mb.box(0.6, rng.range(0.3, 0.4), 0.5, stub, place(-0.15, 0.45, 0.45));
  const cap = { all: p.color('concrete.hi'), top: p.color('concrete.mid') };
  mb.box(1.1, 0.3, 1.4, cap, place(0.8, WATERLINE - 0.45, 0.2, 0.2, 1, 0, -0.55));
  for (let i = 0; i < 4; i++) {
    const a = rng.range(0, TAU);
    const at = place(Math.sin(a) * 1.05, WATERLINE - 0.4, Math.cos(a) * 1.0, a, 1, rng.range(-0.3, 0.3));
    mb.box(rng.range(0.3, 0.55), rng.range(0.4, 0.6), rng.range(0.3, 0.55), stub, at);
  }
  return mb;
};

/** Laboratory: the plinth, pale panels leaning in over the benches, the roof down in two pieces, the air handler off. */
export const laboratoryWreck: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  mb.box(8.0, 0.25, 6.0, { all: p.color('concrete.lo'), top: p.color('ink.1') });
  const panel = { all: p.color('metal.hi'), top: p.color('ink.1'), front: p.color('metal.mid') };
  for (let i = 0; i < 4; i++) mb.box(1.85, rng.range(0.5, 1.8), 0.1, panel, place(-2.85 + i * 1.9, 0.25, -2.75));
  mb.box(0.1, 2.0, 4.6, panel, place(-3.5, 0.25, 0, 0, 1, 0, -0.55));
  mb.box(2.4, 0.1, 4.8, { all: p.color('metal.mid'), top: p.color('metal.hi') }, place(2.4, 0.55, 0, 0.05, 1, 0, 0.12));
  for (const z of [-1.2, 0.9]) mb.box(3.4, 0.7, 0.7, burnt(p), place(-0.6, 0.25, z, rng.range(-0.1, 0.1)));
  const roof = { all: p.color('ink.1'), top: p.color('roof.0') };
  mb.box(3.2, 0.2, 2.6, roof, place(-1.0, 1.0, -0.6, 0.1, 1, 0.3, -0.15));
  mb.box(2.6, 0.2, 2.2, roof, place(1.4, 0.75, 1.3, -0.2, 1, -0.25, 0.1));
  mb.box(1.5, 0.6, 1.0, { all: p.color('metal.mid'), top: p.color('ink.1') }, place(2.6, 0.2, -1.9, 0.5, 1, 0.3));
  debris(mb, p, rng, 8, 3.4, 2.5);
  return mb;
};

/** Safe: scorched, knocked askew, its door gone and nothing but char inside - the papers went with it. */
export const safeWreck: KitBuilder = (p: Palette, _rng: Pcg32) => {
  const mb = new MeshBuilder();
  mb.box(0.98, 0.1, 0.94, p.color('ink.1'));
  mb.with(place(0, 0.08, 0, 0.2, 1, 0, 0.12), () => {
    mb.box(0.94, 1.0, 0.9, burnt(p));
    mb.box(0.7, 0.8, 0.06, p.color('ink.0'), place(0, 0.1, 0.43));
  });
  mb.box(0.22, 0.04, 0.16, p.color('paper.0'), place(0.2, 0.1, 0.48, 0.6));
  return mb;
};
